import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';

const languages = [
  { code: 'fr', label: 'Francais', flag: 'FR' },
  { code: 'en', label: 'English', flag: 'GB' },
  { code: 'es', label: 'Espanol', flag: 'ES' },
  { code: 'it', label: 'Italiano', flag: 'IT' },
  { code: 'de', label: 'Deutsch', flag: 'DE' },
];

const flagEmoji = {
  FR: '\u{1F1EB}\u{1F1F7}',
  GB: '\u{1F1EC}\u{1F1E7}',
  ES: '\u{1F1EA}\u{1F1F8}',
  IT: '\u{1F1EE}\u{1F1F9}',
  DE: '\u{1F1E9}\u{1F1EA}',
};

const LanguageSelector = ({ mobile = false }) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = languages.find(l => l.code === i18n.language?.substring(0, 2)) || languages[0];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  if (mobile) {
    return (
      <div className="space-y-1 px-2 py-2">
        <p className="text-xs text-slate-400 uppercase font-heading tracking-wider px-2 mb-1">
          <Globe className="w-3 h-3 inline mr-1" /> Langue
        </p>
        <div className="flex flex-wrap gap-1.5 px-2">
          {languages.map(lang => (
            <button
              key={lang.code}
              onClick={() => handleChange(lang.code)}
              className={`text-xs px-2.5 py-1.5 font-heading font-bold uppercase tracking-wider transition-all ${
                current.code === lang.code
                  ? 'bg-brand text-white'
                  : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
              data-testid={`lang-mobile-${lang.code}`}
            >
              <span className="mr-1">{flagEmoji[lang.flag]}</span>
              {lang.code.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-white/80 hover:text-white transition-colors px-2 py-1.5"
        data-testid="language-selector"
      >
        <span className="text-sm">{flagEmoji[current.flag]}</span>
        <span className="text-xs font-heading font-bold uppercase tracking-wider">{current.code}</span>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 bg-asphalt border border-white/10 shadow-xl z-50 min-w-[150px] py-1">
          {languages.map(lang => (
            <button
              key={lang.code}
              onClick={() => handleChange(lang.code)}
              className={`w-full text-left px-4 py-2 flex items-center gap-2.5 transition-colors text-sm ${
                current.code === lang.code
                  ? 'bg-brand/20 text-brand'
                  : 'text-white/70 hover:bg-white/10 hover:text-white'
              }`}
              data-testid={`lang-${lang.code}`}
            >
              <span>{flagEmoji[lang.flag]}</span>
              <span className="font-heading font-bold uppercase tracking-wider text-xs">{lang.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
